import type { ReactNode } from "react";
import { Pill } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KioskTextareaField } from "../../../../../components/kiosk-field";
import { PrescriptionSectionHeading } from "./prescription-section-heading";

/**
 * Renders the neutral free-text prescription request primitive with the kiosk
 * textarea standard (icon, floating label, FI-44).
 *
 * eGK callers may compose history controls through `children`; the manual
 * composition intentionally supplies none.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <PrescriptionRequestSection
 *   requestText=""
 *   onRequestTextChange={() => undefined}
 * />
 * ```
 */
export function PrescriptionRequestSection({
  requestText,
  onRequestTextChange,
  children,
}: {
  readonly requestText: string | undefined;
  readonly onRequestTextChange: (text: string) => void;
  readonly children?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <PrescriptionSectionHeading
        number={2}
        title={t("self-service.prescription.medication-selection.medications-title")}
        hint={t("self-service.prescription.medication-selection.request-hint")}
      />
      {children}
      <KioskTextareaField
        id="prescription-request-text"
        testId="frontdesk-prescription-medication-request-input"
        label={t("self-service.prescription.request-review.request-label")}
        icon={Pill}
        required
        rows={4}
        value={requestText ?? ""}
        onChange={onRequestTextChange}
      />
    </section>
  );
}
